<template>
  <Tag
    :value="statusConfig.label"
    :class="[statusConfig.class, 'whitespace-nowrap']"
    :severity="statusConfig.severity"
  >
    <span :class="[statusConfig.icon, 'mr-1']"></span>
    {{ statusConfig.label }}
  </Tag>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import type { TicketStatusBadgeProps } from './ticketStatusBadgeTypes';

const { t } = useI18n();
const statusMap = {
  new: {
    label: t(`enums.TICKETSTATUS_NEW`),
    icon: 'mdi mdi-star',
    severity: 'success',
    class: 'status-new',
  },
  open: {
    label: t(`enums.TICKETSTATUS_OPEN`),
    icon: 'mdi mdi-tag-text-outline',
    severity: 'primary',
    class: 'status-open',
  },
  open_overdue: {
    label: t(`enums.TICKETSTATUS_OPEN`),
    icon: 'mdi mdi-tag-text',
    severity: 'warn',
    class: 'status-open_overdue',
  },
  open_overdue_warn: {
    label: t(`enums.TICKETSTATUS_OVERDUE_WARN`),
    icon: 'mdi mdi-tag-text',
    severity: 'danger',
    class: 'status-open_overdue_warn',
  },
  in_progress: {
    label: t(`enums.TICKETSTATUS_IN_PROGRESS`),
    icon: 'mdi mdi-progress-tag',
    severity: 'primary',
    class: 'status-in_progress',
  },
  in_progress_overdue: {
    label: t(`enums.TICKETSTATUS_IN_PROGRESS_OVERDUE`),
    icon: 'mdi mdi-progress-tag',
    severity: 'warn',
    class: 'status-in_progress_overdue',
  },
  in_progress_overdue_warn: {
    label: t(`enums.TICKETSTATUS_IN_PROGRESS_OVERDUE_WARN`),
    icon: 'mdi mdi-progress-tag',
    severity: 'danger',
    class: 'status-in_progress_overdue_warn',
  },
  closed: {
    label: t(`enums.TICKETSTATUS_CLOSED`),
    icon: 'mdi mdi-tag-check-outline _mdi-thumb-up-outline',
    severity: 'info',
    class: 'status-closed',
  },
};

const props = defineProps<TicketStatusBadgeProps>();
const statusConfig = computed(() => {
  const normalizedStatus = props.status.toLowerCase();
  const matchedKey = Object.keys(statusMap).find((key) => key.toLowerCase() === normalizedStatus);

  return matchedKey
    ? statusMap[matchedKey as keyof typeof statusMap]
    : {
        label: props.status,
        icon: 'pi pi-question-circle',
        severity: 'secondary',
        class: 'status-unknown',
      };
});
</script>

<style scoped lang="css">
@config "../../../../tailwind.config.js";
@reference "tailwindcss";

/* gemeinsames Badge-Layout */
.status-badge {
  @apply flex items-center gap-1 px-2 py-1;
}

/* States – nur Utilities, kein theme() */
.status-new {
  @apply text-white border bg-interactiongreen-500 border-interactiongreen-500;
}

.status-open {
  @apply text-interactiongreen-500 border border-interactiongreen-500 bg-white;
}

.status-open_overdue {
  @apply text-signalorange-500 border border-signalorange-500 bg-white;
}

.status-open_overdue_warn {
  @apply text-signalred-500 border border-signalred-500 bg-white;
}

.status-in_progress {
  @apply text-interactiongreen-500 border border-interactiongreen-500 bg-white;
}

.status-in_progress_overdue {
  @apply text-signalorange-500 border border-signalorange-500 bg-white;
}

.status-in_progress_overdue_warn {
  @apply text-signalred-500 border border-signalred-500 bg-white;
}
</style>
